import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { Loader2, Music, Play, Eye, ArrowLeft, Pause } from "lucide-react";
import { toast } from "sonner";

/**
 * Plyx Sound detail page — /plyx/sound/:soundId
 * Plays the sound preview, shows usage stats, lists all videos that use
 * the sound, and gives creators a "Use this sound" CTA.
 */
const PlyxSoundPage = () => {
  const { soundId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    plyxApi
      .get(`/plyx/sounds/${encodeURIComponent(soundId)}/videos?limit=30`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load sound"))
      .finally(() => setLoading(false));
  }, [soundId]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); setPlaying(false); }
    else { el.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-white/30" />
      </div>
    );
  }
  if (!data?.sound) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <div className="text-center">
          <Music className="w-12 h-12 mx-auto text-white/30 mb-3" />
          <div>Sound not found.</div>
          <Link to="/plyx/feed" className="text-sm text-white/55 underline mt-2 inline-block">Back to feed</Link>
        </div>
      </div>
    );
  }
  const sound = data.sound;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-24" data-testid="plyx-sound-page">
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 opacity-50 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 80% 10%, #00E0C7 0%, transparent 40%), radial-gradient(circle at 10% 90%, #A45DFF 0%, transparent 40%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-5 py-10">
          <Link to="/plyx/feed" className="inline-flex items-center gap-1 text-xs text-white/55 hover:text-white mb-3">
            <ArrowLeft className="w-3.5 h-3.5" /> Plyx feed
          </Link>
          <div className="flex items-center gap-4">
            <button
              onClick={togglePlay}
              data-testid="plyx-sound-play-toggle"
              className="w-16 h-16 rounded-2xl bg-white text-white flex items-center justify-center hover:scale-105 transition-transform"
            >
              {playing ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-1" />}
            </button>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FFB42D] inline-flex items-center gap-1">
                <Music className="w-3 h-3" /> Sound
              </div>
              <h1 className="font-display text-2xl sm:text-3xl tracking-tight leading-tight mt-1" data-testid="plyx-sound-title">
                {sound.title}
              </h1>
              {sound.creator_handle && (
                <div className="text-sm text-white/70 mt-1">
                  Original by{" "}
                  <Link to={`/plyx/u/${sound.creator_handle}`} className="font-semibold text-white hover:text-[#FFB42D]">
                    @{sound.creator_handle}
                  </Link>
                </div>
              )}
            </div>
          </div>
          <audio
            ref={audioRef}
            src={plyxMediaUrl(sound.audio_url)}
            onEnded={() => setPlaying(false)}
            preload="metadata"
            playsInline
          />
          <div className="mt-5 flex items-center gap-5 text-sm text-white/70">
            <span className="inline-flex items-center gap-1.5"><Play className="w-4 h-4" /> {sound.usage_count.toLocaleString()} videos</span>
            {sound.duration_sec > 0 && <span>{Math.round(sound.duration_sec)}s</span>}
          </div>
          <Link
            to={`/plyx/upload?sound_id=${encodeURIComponent(sound.sound_id)}`}
            data-testid="plyx-sound-use-cta"
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-white text-sm"
            style={{ background: "linear-gradient(135deg, #00E0C7 0%, #A45DFF 100%)" }}
          >
            <Music className="w-4 h-4" /> Use this sound
          </Link>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 py-8">
        {data.items.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-12 text-center">
            <Music className="w-12 h-12 mx-auto text-white/30 mb-3" />
            <div className="font-semibold">No videos using this sound yet.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {data.items.map((v) => (
              <Link
                key={v.video_id}
                to={`/plyx/v/${v.video_id}`}
                data-testid={`plyx-sound-video-${v.video_id}`}
                className="group block aspect-[9/16] relative rounded-xl overflow-hidden bg-black/60 border border-white/5 hover:border-white/25 transition-colors"
              >
                {v.thumbnail_url ? (
                  <img src={plyxMediaUrl(v.thumbnail_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30">
                    <Play className="w-10 h-10" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-2 left-2 right-2 text-[11px] line-clamp-2 leading-tight font-medium">
                  {v.caption || ""}
                </div>
                <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/55 backdrop-blur text-[10px] font-semibold">
                  <Eye className="w-2.5 h-2.5" /> {(v.view_count || 0).toLocaleString()}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlyxSoundPage;
